import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { CARD_BACKS, TABLE_FELTS, type CardBackId, type CosmeticKind, type TableFeltId } from '@cardhub/protocol';
import { api } from '../api/client';
import { CardBack, PlayingCard } from '../cards/PlayingCard';
import { PayPalCheckout, usePayPalClientId } from '../components/PayPalCheckout';
import { Coins, TopBar } from '../components/ui';
import { isNative } from '../native/platform';
import { buyNative, loadLocalizedPrices, type StoreProduct } from '../native/purchases';
import { useT, type MessageKey } from '../i18n';
import { errorMessage, useAsync } from '../lib/hooks';
import { saveSettings, useSession } from '../store/session';
import { useSettings } from '../store/settings';
import { toast } from '../store/ui';

export const FELT_BG: Record<TableFeltId, { bg: string; rim: string }> = {
  emerald: { bg: 'radial-gradient(#0f6b4f, #0a4636)', rim: '#3b2a17' },
  royal: { bg: 'radial-gradient(#1f4aa8, #0f2257)', rim: '#2b1d0e' },
  maroon: { bg: 'radial-gradient(#8f1d2c, #4a0a14)', rim: '#2a1a0c' },
  charcoal: { bg: 'radial-gradient(#3f4654, #151a23)', rim: '#57401f' },
  teal: { bg: 'radial-gradient(#138a80, #064e4b)', rim: '#3b2a17' },
};

export interface Owned {
  cardBacks: string[];
  tableFelts: string[];
}
interface CosmeticProduct {
  kind: CosmeticKind;
  itemId: string;
  productId: string;
  sku: string;
  priceMicros: number;
  currency: string;
  platformSkus: Record<string, string>;
}
type CosmeticsResponse = Owned & { products: CosmeticProduct[]; enabled: boolean };

/** Real-money purchases: native store sheets in the apps, PayPal on the web. Until one is set up, items show "Coming soon". */
export const useCanBuy = () => {
  const paypal = usePayPalClientId();
  return isNative || Boolean(paypal);
};

export function useCosmetics() {
  const q = useAsync(() => api.get<CosmeticsResponse>('/api/v1/store/cosmetics'), []);
  const [local, setLocal] = useState<Record<string, StoreProduct>>({});
  useEffect(() => {
    if (!q.data) return;
    const base = q.data.products.map((p) => ({ id: p.productId, sku: p.sku, name: p.sku, coins: 0, bonusCoins: 0, priceMicros: p.priceMicros, currency: p.currency, badge: null, platformSkus: p.platformSkus }));
    void loadLocalizedPrices(base).then((items) => setLocal(Object.fromEntries(items.map((i) => [i.sku, i]))));
  }, [q.data]);
  const productFor = (kind: CosmeticKind, itemId: string) => {
    const p = q.data?.products.find((x) => x.kind === kind && x.itemId === itemId);
    return p ? (local[p.sku] ?? null) : null;
  };
  return { owned: q.data, enabled: q.data?.enabled ?? true, productFor, reload: q.reload };
}

export function rarityOf(cents: number): { name: string; color: string } {
  if (cents <= 0) return { name: 'Standard', color: '#94a3b8' };
  if (cents <= 199) return { name: 'Rare', color: '#38bdf8' };
  if (cents <= 499) return { name: 'Epic', color: '#c084fc' };
  return { name: 'Legendary', color: '#fbbf24' };
}

/** Spotlit stage: the chosen back front-and-centre, flanked by standard-design high cards. */
function CardPreview({ id, color }: { id: CardBackId; color: string }) {
  return (
    <div className="shop-stage" style={{ ['--glow' as string]: color }} aria-hidden>
      <span className="shop-fan l"><PlayingCard id="AS" size="lg" theme={id} /></span>
      <span className="shop-fan r"><PlayingCard id="KH" size="lg" theme={id} /></span>
      <span className="shop-hero"><CardBack variant={id} /></span>
    </div>
  );
}

const FELT_ACCENT: Record<TableFeltId, string> = { emerald: '#34d399', royal: '#60a5fa', maroon: '#fb7185', charcoal: '#fbbf24', teal: '#2dd4bf' };

/** A tilted table: wood rim, gold inlay, felt with lighting and a vignette, cards and chips on top. */
function TablePreview({ id, color }: { id: TableFeltId; color: string }) {
  const f = FELT_BG[id];
  const accent = FELT_ACCENT[id];
  return (
    <div className="shop-stage" style={{ ['--glow' as string]: color }} aria-hidden>
      <div className="shop-table" style={{ ['--rim' as string]: f.rim, ['--accent' as string]: accent }}>
        <div className="shop-felt" style={{ background: f.bg }}>
          <span className="shop-felt-mark" style={{ color: accent }}>♠ ♥ ♦ ♣</span>
          <div className="shop-felt-cards">
            {['AS', 'KS', 'QS'].map((c, i) => (
              <span key={c} style={{ marginLeft: i ? -26 : 0, transform: `rotate(${(i - 1) * 9}deg)` }}>
                <PlayingCard id={c} size="md" />
              </span>
            ))}
          </div>
          <span className="shop-chips">
            <i style={{ background: '#ef4444' }} /><i style={{ background: '#f8fafc' }} /><i style={{ background: accent }} />
          </span>
        </div>
      </div>
    </div>
  );
}

const usd = (micros: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(micros / 1e6);

export function ShopItem({ kind, id, cents, owned, product, enabled, onBought }: { kind: CosmeticKind; id: string; cents: number; owned: boolean; product: StoreProduct | null; enabled: boolean; onBought: () => void }) {
  const t = useT();
  const user = useSession((s) => s.user)!;
  const settings = useSettings((s) => s.settings);
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const canBuy = useCanBuy();
  const active = kind === 'CARD_BACK' ? settings.cardBack === id : settings.tableFelt === id;
  const name = t((kind === 'CARD_BACK' ? `cosmetics.back.${id}` : `cosmetics.felt.${id}`) as MessageKey);
  const rarity = rarityOf(cents);
  const applyItem = () => saveSettings(kind === 'CARD_BACK' ? { cardBack: id as CardBackId } : { tableFelt: id as TableFeltId });
  const price = product ? (product.localizedPrice ?? usd(product.priceMicros)) : usd(cents * 10000);

  const buy = async () => {
    if (!product) return;
    if (!isNative) {
      setPaying(true);
      return;
    }
    setBusy(true);
    try {
      await buyNative(product, user.id);
      onBought();
      await applyItem();
      toast(`${name} unlocked`, 'success');
    } catch (e) {
      if (!/cancel/i.test(String((e as Error)?.message ?? ''))) toast(errorMessage(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`card col shop-item ${cents >= 500 ? 'legend' : ''}`} style={{ textAlign: 'center', gap: 8, ['--glow' as string]: rarity.color, outline: active ? '2px solid var(--accent)' : undefined }}>
      <span className="shop-rarity" style={{ background: rarity.color }}>{rarity.name}</span>
      {kind === 'CARD_BACK' ? <CardPreview id={id as CardBackId} color={rarity.color} /> : <TablePreview id={id as TableFeltId} color={rarity.color} />}
      <strong>{name}</strong>
      {owned ? (
        <button className={`btn block ${active ? '' : 'primary'}`} disabled={active} onClick={() => void applyItem()}>
          {active ? 'In use' : 'Use'}
        </button>
      ) : (
        <button className="btn gold block" disabled={busy || !product || !enabled || !canBuy} onClick={() => void buy()}>
          {busy ? t('common.loading') : canBuy ? `Buy · ${price}` : `${price} · Coming soon`}
        </button>
      )}
      {paying && product && (
        <PayPalCheckout
          open
          title={name}
          priceLabel={price}
          productId={product.id}
          onClose={() => setPaying(false)}
          onPaid={() => {
            setPaying(false);
            onBought();
            void applyItem();
            toast(`${name} unlocked`, 'success');
          }}
        />
      )}
    </div>
  );
}

export function Shop() {
  const nav = useNavigate();
  const balance = useSession((s) => s.balance);
  const [tab, setTab] = useState<'cards' | 'tables'>('cards');
  const cosmetics = useCosmetics();
  const canBuy = useCanBuy();
  const owned = cosmetics.owned;
  const tabs: [typeof tab, string][] = [['cards', 'Cards'], ['tables', 'Tables']];

  return (
    <main className="page">
      <TopBar title="Shop" onBack={() => nav(-1)} right={<Coins amount={balance} />} />
      <div className="segmented" role="group" aria-label="Shop sections" style={{ marginBottom: 14 }}>
        {tabs.map(([k, label]) => (
          <button key={k} aria-pressed={tab === k} onClick={() => setTab(k)}>{label}</button>
        ))}
        <Link to="/store" className="btn ghost">Coins</Link>
      </div>
      {!canBuy && (
        <div className="card" style={{ marginBottom: 14, textAlign: 'center' }}>
          <strong>✨ Purchases are opening soon</strong>
          <div className="dim" style={{ fontSize: 'var(--fs-sm)' }}>Take a look around. Everything here will be available to unlock very shortly.</div>
        </div>
      )}
      <div className="games-grid">
        {tab === 'cards'
          ? CARD_BACKS.map((c) => <ShopItem key={c.id} kind="CARD_BACK" id={c.id} cents={c.price} owned={owned ? owned.cardBacks.includes(c.id) : c.price === 0} product={cosmetics.productFor('CARD_BACK', c.id)} enabled={cosmetics.enabled} onBought={cosmetics.reload} />)
          : TABLE_FELTS.map((c) => <ShopItem key={c.id} kind="TABLE_FELT" id={c.id} cents={c.price} owned={owned ? owned.tableFelts.includes(c.id) : c.price === 0} product={cosmetics.productFor('TABLE_FELT', c.id)} enabled={cosmetics.enabled} onBought={cosmetics.reload} />)}
      </div>
    </main>
  );
}
